import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import {
  Plus, Trash2, Zap, Edit2, Play, ToggleLeft, ToggleRight, BarChart3,
  CheckCircle2, XCircle, Loader2, Send, MessageSquare, Webhook, Tag,
  ArrowRight, Filter, Target, AlertCircle, Clock, Activity
} from 'lucide-react';
import { cn } from '../lib/utils';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_OPTIONS = [
  { value: 'utm_source', label: 'UTM Source' },
  { value: 'utm_medium', label: 'UTM Medium' },
  { value: 'utm_campaign', label: 'UTM Campaign' },
  { value: 'utm_content', label: 'UTM Content' },
  { value: 'email', label: 'Email' },
  { value: 'email_domain', label: 'Email Domain' },
  { value: 'is_business_email', label: 'Is Business Email' },
  { value: 'has_phone', label: 'Has Phone' },
  { value: 'has_email', label: 'Has Email' },
  { value: 'company', label: 'Company' },
  { value: 'message', label: 'Message Body' },
  { value: 'country', label: 'Country' },
  { value: 'score', label: 'Lead Score (0-100)' },
  { value: 'source', label: 'Source' },
  { value: 'day_of_week', label: 'Day of Week (0=Mon)' },
  { value: 'hour', label: 'Hour (UTC)' },
];

const OP_OPTIONS = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
  { value: 'in', label: 'is one of' },
  { value: 'not_in', label: 'is not one of' },
  { value: 'gte', label: '≥' },
  { value: 'lte', label: '≤' },
  { value: 'exists', label: 'exists' },
];

const ACTION_TYPES = [
  { value: 'assign', label: 'Assign to user/team', icon: Target, color: 'emerald' },
  { value: 'set_stage', label: 'Set pipeline stage', icon: ArrowRight, color: 'blue' },
  { value: 'add_tags', label: 'Add tags', icon: Tag, color: 'amber' },
  { value: 'score_bonus', label: 'Score bonus', icon: BarChart3, color: 'violet' },
  { value: 'notify_in_app', label: 'In-app notification', icon: AlertCircle, color: 'sky' },
  { value: 'notify_slack', label: 'Slack message', icon: MessageSquare, color: 'pink' },
  { value: 'notify_whatsapp', label: 'WhatsApp message', icon: MessageSquare, color: 'green' },
  { value: 'notify_email', label: 'Email notification', icon: Send, color: 'indigo' },
  { value: 'webhook', label: 'Outbound webhook', icon: Webhook, color: 'gray' },
];

const STAGE_OPTIONS = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'];

const TEMPLATES = [
  {
    name: 'High-intent paid traffic → top rep',
    description: 'Leads from CPC ads with score ≥ 60 go to your best closer + Slack ping',
    rule: {
      name: 'High-intent paid → top rep',
      priority: 10,
      match: 'all',
      conditions: [
        { field: 'utm_medium', op: 'in', value: ['cpc', 'ppc', 'paid'] },
        { field: 'score', op: 'gte', value: 60 },
      ],
      actions: [
        { type: 'add_tags', tags: ['hot', 'paid-channel'] },
        { type: 'set_stage', stage: 'qualified' },
        { type: 'notify_in_app', title: 'Hot paid lead', body: 'Score ≥ 60, paid traffic — call within 5 min' },
      ],
    },
  },
  {
    name: 'Facebook campaign routing',
    description: 'Any lead with utm_source=facebook gets tagged + assigned',
    rule: {
      name: 'Facebook campaign routing',
      priority: 20,
      match: 'all',
      conditions: [{ field: 'utm_source', op: 'eq', value: 'facebook' }],
      actions: [
        { type: 'add_tags', tags: ['facebook', 'social'] },
        { type: 'notify_in_app', title: 'New FB lead', body: '' },
      ],
    },
  },
  {
    name: 'Business email → priority',
    description: 'Non-Gmail/Yahoo emails get +20 score and "B2B" tag',
    rule: {
      name: 'Business email → priority',
      priority: 30,
      match: 'all',
      conditions: [{ field: 'is_business_email', op: 'eq', value: true }],
      actions: [
        { type: 'add_tags', tags: ['b2b'] },
        { type: 'score_bonus', amount: 20 },
      ],
    },
  },
];

const EMPTY_RULE = {
  name: '',
  enabled: true,
  priority: 100,
  match: 'all',
  stop_on_match: false,
  conditions: [{ field: 'utm_source', op: 'eq', value: '' }],
  actions: [{ type: 'notify_in_app', title: '', body: '' }],
  description: '',
};

export default function LeadAutomationPage() {
  const [rules, setRules] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [tab, setTab] = useState('rules'); // rules | runs
  const [runs, setRuns] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [r, d] = await Promise.all([
        axios.get(`${API}/api/lead-automation/rules`),
        axios.get(`${API}/api/lead-automation/dashboard?days=30`),
      ]);
      setRules(r.data || []);
      setDashboard(d.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
    setLoading(false);
  };

  const loadRuns = async () => {
    try {
      const { data } = await axios.get(`${API}/api/lead-automation/runs?limit=50`);
      setRuns(data || []);
    } catch {}
  };

  useEffect(() => { load(); loadRuns(); }, []);

  const handleToggle = async (rule) => {
    try {
      await axios.put(`${API}/api/lead-automation/rules/${rule.id}`, { ...rule, enabled: !rule.enabled });
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/lead-automation/rules/${rule.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="space-y-5 animate-fadeIn" data-testid="lead-automation-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Zap className="h-6 w-6 text-amber-500" />Lead Routing & Automation<PageInfoTooltip pageKey="lead-automation" /></h1>
          <p className="text-sm text-gray-500">Auto-route, score, and notify on every new lead — UTM-aware, real-time, no manual triage.</p>
        </div>
        <Button className="gap-2 bg-emerald-600 hover:bg-emerald-500" onClick={() => setEditingRule({ ...EMPTY_RULE })} data-testid="new-rule-btn">
          <Plus className="h-4 w-4" />New Rule
        </Button>
      </div>

      {/* KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase tracking-wider">Active Rules</p><p className="text-2xl font-bold mt-1">{dashboard.rules_enabled}<span className="text-sm text-gray-400 font-normal"> / {dashboard.rules_total}</span></p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase tracking-wider">Leads Processed (30d)</p><p className="text-2xl font-bold mt-1 text-blue-600">{dashboard.runs_total}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase tracking-wider">Auto-Routed</p><p className="text-2xl font-bold mt-1 text-emerald-600">{dashboard.runs_matched}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500 uppercase tracking-wider">Match Rate</p><p className="text-2xl font-bold mt-1 text-violet-600">{dashboard.match_rate}%</p></CardContent></Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button onClick={() => setTab('rules')} className={cn('px-4 py-2 text-sm font-medium border-b-2 transition-colors', tab === 'rules' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700')} data-testid="tab-rules">
          Rules ({rules.length})
        </button>
        <button onClick={() => { setTab('runs'); loadRuns(); }} className={cn('px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5', tab === 'runs' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700')} data-testid="tab-runs">
          <Activity className="h-3.5 w-3.5" />Recent runs
        </button>
      </div>

      {tab === 'rules' && (
        <>
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>
          ) : rules.length === 0 ? (
            <Card><CardContent className="p-8 text-center">
              <Zap className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-base font-semibold mb-1">No rules yet</p>
              <p className="text-sm text-gray-500 mb-5">Pick a starter template, or build a rule from scratch.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-3xl mx-auto">
                {TEMPLATES.map((t, i) => (
                  <button key={i} onClick={() => setEditingRule({ ...EMPTY_RULE, ...t.rule, enabled: true })}
                    className="text-left p-3 border border-gray-200 rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition" data-testid={`template-${i}`}>
                    <p className="text-xs font-semibold mb-1">{t.name}</p>
                    <p className="text-[10px] text-gray-500 leading-snug">{t.description}</p>
                  </button>
                ))}
              </div>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {rules.map(rule => (
                <Card key={rule.id} className={cn(rule.enabled ? 'border-l-4 border-l-emerald-500' : 'opacity-60')}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <button onClick={() => handleToggle(rule)} title={rule.enabled ? 'Disable' : 'Enable'} data-testid={`toggle-${rule.id}`}>
                      {rule.enabled ? <ToggleRight className="h-6 w-6 text-emerald-500" /> : <ToggleLeft className="h-6 w-6 text-gray-300" />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm truncate">{rule.name}</p>
                        <Badge className="bg-gray-100 text-gray-700 text-[10px]">priority {rule.priority}</Badge>
                        {rule.match === 'any' && <Badge className="bg-amber-100 text-amber-700 text-[10px]">match ANY</Badge>}
                        {rule.stop_on_match && <Badge className="bg-red-100 text-red-700 text-[10px]">stop after</Badge>}
                        {rule.fire_count > 0 && <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Zap className="h-2.5 w-2.5" />{rule.fire_count}x</Badge>}
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {rule.conditions.length} {rule.conditions.length === 1 ? 'condition' : 'conditions'} · {rule.actions.length} {rule.actions.length === 1 ? 'action' : 'actions'}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={() => setEditingRule(rule)} data-testid={`edit-${rule.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />Edit
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-red-500 hover:text-red-600" onClick={() => handleDelete(rule)} data-testid={`delete-${rule.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'runs' && (
        <Card><CardContent className="p-3">
          {runs.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">No automation runs yet. Submit a lead via a landing-page form to test.</p>
          ) : (
            <div className="text-[12px] border rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 border-b font-semibold text-gray-500 uppercase tracking-wider text-[9px]">
                <div className="col-span-3">When</div>
                <div className="col-span-3">Lead</div>
                <div className="col-span-3">Matched rules</div>
                <div className="col-span-3">Actions run</div>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {runs.map(r => (
                  <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b last:border-0 hover:bg-gray-50">
                    <div className="col-span-3 font-mono text-gray-500 text-[10px]">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</div>
                    <div className="col-span-3 truncate">
                      <p className="font-medium">{r.lead_name || '(no name)'}</p>
                      <p className="text-[10px] text-gray-400">{r.lead_email}</p>
                    </div>
                    <div className="col-span-3 truncate">
                      {r.matched.length > 0
                        ? r.matched.map(m => <Badge key={m.rule_id} className="bg-emerald-100 text-emerald-700 text-[10px] mr-1">{m.name}</Badge>)
                        : <span className="text-[10px] text-gray-400 italic">no rules matched</span>}
                    </div>
                    <div className="col-span-3 truncate">
                      {r.actions_run.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {r.actions_run.map((a, i) => (
                            <span key={i} className={cn('text-[10px] px-1.5 py-0.5 rounded',
                              a.ok === false ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700')} title={a.reason || a.error || ''}>
                              {a.ok === false ? <XCircle className="h-2.5 w-2.5 inline mr-0.5" /> : <CheckCircle2 className="h-2.5 w-2.5 inline mr-0.5" />}
                              {a.type}
                            </span>
                          ))}
                        </div>
                      ) : <span className="text-[10px] text-gray-400 italic">—</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent></Card>
      )}

      {/* Rule editor */}
      <RuleEditor open={!!editingRule} rule={editingRule} onClose={() => setEditingRule(null)} onSaved={load} />
    </div>
  );
}

function RuleEditor({ open, rule: initialRule, onClose, onSaved }) {
  const [rule, setRule] = useState(initialRule);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => { setRule(initialRule); setTestResult(null); }, [initialRule]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/users`).then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [open]);

  if (!open || !rule) return null;

  const set = (patch) => setRule(prev => ({ ...prev, ...patch }));

  const updateCond = (i, patch) => {
    const conds = [...rule.conditions];
    conds[i] = { ...conds[i], ...patch };
    set({ conditions: conds });
  };
  const addCond = () => set({ conditions: [...rule.conditions, { field: 'utm_source', op: 'eq', value: '' }] });
  const removeCond = (i) => set({ conditions: rule.conditions.filter((_, j) => j !== i) });

  const updateAction = (i, patch) => {
    const acts = [...rule.actions];
    acts[i] = { ...acts[i], ...patch };
    set({ actions: acts });
  };
  const addAction = () => set({ actions: [...rule.actions, { type: 'notify_in_app', title: '', body: '' }] });
  const removeAction = (i) => set({ actions: rule.actions.filter((_, j) => j !== i) });

  const handleSave = async () => {
    if (!rule.name.trim()) { toast.error('Rule needs a name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: rule.name.trim(),
        enabled: rule.enabled,
        priority: rule.priority,
        match: rule.match,
        stop_on_match: rule.stop_on_match,
        conditions: rule.conditions,
        actions: rule.actions,
        description: rule.description || '',
      };
      if (rule.id) await axios.put(`${API}/api/lead-automation/rules/${rule.id}`, payload);
      else await axios.post(`${API}/api/lead-automation/rules`, payload);
      toast.success('Saved');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTestResult({ loading: true });
    try {
      const sample = {
        utm_source: 'facebook', utm_medium: 'cpc', utm_campaign: 'demo',
        email: 'jane@acme.com', phone: '+15551234', score: 75,
        first_name: 'Jane', last_name: 'Doe', company: 'Acme',
        message: 'I need a demo — please call', country: 'US',
      };
      const { data } = await axios.post(`${API}/api/lead-automation/rules/test`, {
        rule: {
          name: rule.name || 'test',
          enabled: true, priority: 100, match: rule.match, stop_on_match: false,
          conditions: rule.conditions, actions: rule.actions,
        },
        sample_lead: sample,
      });
      setTestResult({ ...data, sample });
    } catch (e) {
      setTestResult({ error: e.response?.data?.detail || 'Test failed' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="rule-editor">
        <DialogHeader><DialogTitle>{rule.id ? 'Edit rule' : 'New rule'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Top row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-2">
              <Label className="text-xs">Name</Label>
              <Input value={rule.name} onChange={e => set({ name: e.target.value })} placeholder="e.g. FB high-intent → Sales A" data-testid="rule-name" />
            </div>
            <div>
              <Label className="text-xs">Priority (lower = first)</Label>
              <Input type="number" value={rule.priority} onChange={e => set({ priority: parseInt(e.target.value || 100) })} data-testid="rule-priority" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <label className="flex items-center gap-2"><Switch checked={rule.enabled} onCheckedChange={v => set({ enabled: v })} data-testid="rule-enabled" />Enabled</label>
            <label className="flex items-center gap-2"><Switch checked={rule.stop_on_match} onCheckedChange={v => set({ stop_on_match: v })} data-testid="rule-stop" />Stop after match</label>
            <Select value={rule.match} onValueChange={v => set({ match: v })}>
              <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Match ALL conditions</SelectItem>
                <SelectItem value="any">Match ANY condition</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Conditions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><Filter className="h-3.5 w-3.5" />Conditions ({rule.match === 'all' ? 'AND' : 'OR'})</p>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addCond} data-testid="add-cond"><Plus className="h-3 w-3" />Condition</Button>
            </div>
            <div className="space-y-1.5">
              {rule.conditions.length === 0 && (
                <p className="text-[11px] text-gray-400 italic p-2 bg-gray-50 rounded">No conditions = catch-all rule (matches every lead).</p>
              )}
              {rule.conditions.map((c, i) => (
                <div key={i} className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded">
                  <Select value={c.field} onValueChange={v => updateCond(i, { field: v })}>
                    <SelectTrigger className="h-7 text-[11px] flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_OPTIONS.map(f => <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={v => updateCond(i, { op: v })}>
                    <SelectTrigger className="h-7 text-[11px] w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>{OP_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  {c.op !== 'exists' && (
                    c.op === 'in' || c.op === 'not_in' ? (
                      <Input className="h-7 text-[11px] flex-1" placeholder="comma,separated,values"
                        value={Array.isArray(c.value) ? c.value.join(',') : (c.value || '')}
                        onChange={e => updateCond(i, { value: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
                    ) : (
                      <Input className="h-7 text-[11px] flex-1" placeholder="value"
                        value={c.value === true ? 'true' : c.value === false ? 'false' : (c.value ?? '')}
                        onChange={e => {
                          const v = e.target.value;
                          updateCond(i, { value: v === 'true' ? true : v === 'false' ? false : v });
                        }} />
                    )
                  )}
                  <button onClick={() => removeCond(i)} className="p-1 hover:bg-red-100 rounded"><Trash2 className="h-3 w-3 text-red-400" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700 flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" />Actions (run in order)</p>
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addAction} data-testid="add-action"><Plus className="h-3 w-3" />Action</Button>
            </div>
            <div className="space-y-2">
              {rule.actions.map((a, i) => (
                <ActionEditor key={i} action={a} users={users} onChange={patch => updateAction(i, patch)} onRemove={() => removeAction(i)} index={i} />
              ))}
            </div>
          </div>

          {/* Test */}
          <div>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={handleTest} data-testid="test-rule-btn"><Play className="h-3.5 w-3.5" />Test against sample lead</Button>
            {testResult?.loading && <p className="text-xs text-gray-500 mt-2">Testing…</p>}
            {testResult?.error && <p className="text-xs text-red-500 mt-2">{testResult.error}</p>}
            {testResult && testResult.matched !== undefined && (
              <div className={cn('mt-2 p-2 rounded text-xs', testResult.matched ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200')} data-testid="test-result">
                <p className="font-semibold mb-1">{testResult.matched ? '✓ Rule WOULD fire' : '✗ Rule would NOT fire'} on the sample lead</p>
                <div className="space-y-0.5 text-[10px] font-mono">
                  {testResult.per_condition.map((c, i) => (
                    <div key={i} className={c.result ? 'text-emerald-700' : 'text-gray-500'}>
                      {c.result ? '✓' : '✗'} {c.condition.field} {c.condition.op} {JSON.stringify(c.condition.value)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-500 gap-2" onClick={handleSave} disabled={saving} data-testid="save-rule-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}Save rule
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ActionEditor({ action, users, onChange, onRemove, index }) {
  const meta = ACTION_TYPES.find(a => a.value === action.type) || ACTION_TYPES[0];
  const Icon = meta.icon;
  return (
    <div className="border border-gray-200 rounded-lg p-2.5 bg-white">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 text-${meta.color}-500`} />
        <Select value={action.type} onValueChange={v => onChange({ type: v })}>
          <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTION_TYPES.map(a => <SelectItem key={a.value} value={a.value} className="text-xs">{a.label}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={onRemove} className="p-1 hover:bg-red-100 rounded" data-testid={`remove-action-${index}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-400" />
        </button>
      </div>

      {action.type === 'assign' && (
        <div className="grid grid-cols-2 gap-1.5">
          <Select value={action.kind || 'user'} onValueChange={v => onChange({ kind: v })}>
            <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="user" className="text-xs">Specific user</SelectItem>
              <SelectItem value="role" className="text-xs">By role (least busy)</SelectItem>
              <SelectItem value="round_robin" className="text-xs">Round-robin</SelectItem>
            </SelectContent>
          </Select>
          {action.kind === 'role' ? (
            <Select value={action.role || ''} onValueChange={v => onChange({ role: v })}>
              <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>{['super_admin', 'admin', 'manager', 'employee'].map(r => <SelectItem key={r} value={r} className="text-xs">{r}</SelectItem>)}</SelectContent>
            </Select>
          ) : action.kind === 'round_robin' ? (
            <Input className="h-7 text-[11px]" placeholder="user_ids comma-separated"
              value={(action.user_ids || []).join(',')}
              onChange={e => onChange({ user_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
          ) : (
            <Select value={action.user_id || ''} onValueChange={v => onChange({ user_id: v })}>
              <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Pick user" /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id || u._id} value={u.id || u._id} className="text-xs">{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {action.type === 'set_stage' && (
        <Select value={action.stage || 'new'} onValueChange={v => onChange({ stage: v })}>
          <SelectTrigger className="h-7 text-[11px] w-full"><SelectValue /></SelectTrigger>
          <SelectContent>{STAGE_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
      )}

      {action.type === 'add_tags' && (
        <Input className="h-7 text-[11px]" placeholder="tag1, tag2"
          value={(action.tags || []).join(', ')}
          onChange={e => onChange({ tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
      )}

      {action.type === 'score_bonus' && (
        <Input type="number" className="h-7 text-[11px] w-32" placeholder="+15"
          value={action.amount || ''} onChange={e => onChange({ amount: parseInt(e.target.value || 0) })} />
      )}

      {action.type === 'notify_in_app' && (
        <div className="grid grid-cols-2 gap-1.5">
          <Input className="h-7 text-[11px]" placeholder="Title" value={action.title || ''} onChange={e => onChange({ title: e.target.value })} />
          <Input className="h-7 text-[11px]" placeholder="Body" value={action.body || ''} onChange={e => onChange({ body: e.target.value })} />
        </div>
      )}

      {action.type === 'notify_slack' && (
        <Input className="h-7 text-[11px] font-mono" placeholder="https://hooks.slack.com/services/..."
          value={action.webhook_url || ''} onChange={e => onChange({ webhook_url: e.target.value })} />
      )}

      {action.type === 'notify_whatsapp' && (
        <div className="grid grid-cols-2 gap-1.5">
          <Input className="h-7 text-[11px]" placeholder="+15551234567" value={action.phone || ''} onChange={e => onChange({ phone: e.target.value })} />
          <Input className="h-7 text-[11px]" placeholder="Message — supports {name} {email} {source} {score}"
            value={action.message || ''} onChange={e => onChange({ message: e.target.value })} />
        </div>
      )}

      {action.type === 'notify_email' && (
        <div className="grid grid-cols-1 gap-1.5">
          <Input className="h-7 text-[11px]" placeholder="to@example.com" value={action.to || ''} onChange={e => onChange({ to: e.target.value })} />
          <Input className="h-7 text-[11px]" placeholder="Subject" value={action.subject || ''} onChange={e => onChange({ subject: e.target.value })} />
          <Textarea className="text-[11px] min-h-[60px]" placeholder="Body" value={action.body || ''} onChange={e => onChange({ body: e.target.value })} />
        </div>
      )}

      {action.type === 'webhook' && (
        <Input className="h-7 text-[11px] font-mono" placeholder="https://your-endpoint.com/leads"
          value={action.url || ''} onChange={e => onChange({ url: e.target.value })} />
      )}
    </div>
  );
}
